import React from 'react';

/**
 * FreeThrowMetricsPanel — Real-time metrics side panel
 *
 * Displays the 3 coach metrics + expanded measurements for a selected attempt.
 * Syncs with video playback in the parent FreeThrowTab.
 */

const OUTCOME_COLORS = {
  made: '#22c55e',
  missed_short: '#ef4444',
  missed_long: '#f97316',
  missed_left: '#8b5cf6',
  missed_right: '#06b6d4',
  missed_rim_in_out: '#eab308',
  missed_airball: '#ec4899',
  not_visible: '#6b7280',
};

const cardStyle = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: 16,
};

const metricBox = (color) => ({
  ...cardStyle,
  borderLeft: `3px solid ${color}`,
  textAlign: 'center',
  minWidth: 100,
});

function MetricCard({ label, value, unit, color, sublabel }) {
  return (
    <div style={metricBox(color)}>
      <div style={{ fontSize: 24, fontWeight: 800, color: '#fff' }}>
        {value ?? '—'}
        {unit && <span style={{ fontSize: 12, fontWeight: 400, color: '#888' }}>{unit}</span>}
      </div>
      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{label}</div>
      {sublabel && <div style={{ fontSize: 10, color: color, marginTop: 2 }}>{sublabel}</div>}
    </div>
  );
}

export default function FreeThrowMetricsPanel({ attempt, playerSummary }) {
  if (!attempt) {
    return (
      <div style={{ ...cardStyle, textAlign: 'center', color: '#888', padding: 40 }}>
        Select an attempt to view metrics
      </div>
    );
  }

  const speed = attempt.inter_phase_speed || {};
  const knee = attempt.knee_angle || {};
  const heel = attempt.heel_contact || {};
  const ft = attempt.follow_through || {};
  const ball = attempt.ball_start || {};
  const outcome = attempt.shot_outcome || 'not_visible';
  const outcomeColor = OUTCOME_COLORS[outcome] || '#6b7280';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Outcome Badge */}
      <div style={{ textAlign: 'center' }}>
        <span style={{
          display: 'inline-block',
          padding: '4px 16px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 700,
          background: `${outcomeColor}22`,
          color: outcomeColor,
          border: `1px solid ${outcomeColor}44`,
        }}>
          {outcome.replace(/_/g, ' ').toUpperCase()}
        </span>
        {attempt.shot_outcome_detail && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
            {attempt.shot_outcome_detail}
          </div>
        )}
      </div>

      {/* 3 Coach Metrics Hero Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <MetricCard
          label="Shot Speed"
          value={speed.total_shot_speed_tenths > 0 ? speed.total_shot_speed_tenths.toFixed(1) : null}
          unit="t"
          color="#6366f1"
          sublabel="raise → release"
        />
        <MetricCard
          label="Knee Angle"
          value={knee.min_knee_angle_degrees != null ? Math.round(knee.min_knee_angle_degrees) : null}
          unit="°"
          color="#f59e0b"
          sublabel={knee.dip_timing !== 'not_detected' ? knee.dip_timing.replace(/_/g, ' ') : null}
        />
        <MetricCard
          label="Toe Duration"
          value={heel.toe_duration_tenths > 0 ? heel.toe_duration_tenths.toFixed(1) : null}
          unit="t"
          color="#22c55e"
          sublabel={heel.landing_type !== 'not_visible' ? heel.landing_type : null}
        />
      </div>

      {/* Ball Start Position */}
      <div style={cardStyle}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#888' }}>Ball Start Position</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, fontSize: 12 }}>
          <div>
            <span style={{ color: '#888' }}>Height: </span>
            <span style={{ color: '#fff' }}>{ball.height_relative_to_body || '—'}</span>
          </div>
          <div>
            <span style={{ color: '#888' }}>Lateral: </span>
            <span style={{ color: '#fff' }}>{ball.lateral_position || '—'}</span>
          </div>
          <div>
            <span style={{ color: '#888' }}>Dist: </span>
            <span style={{ color: '#fff' }}>{ball.distance_from_body || '—'}</span>
          </div>
        </div>
      </div>

      {/* Follow-Through */}
      <div style={cardStyle}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#888' }}>Follow-Through</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12 }}>
          <div>
            <span style={{ color: '#888' }}>Hold: </span>
            <span style={{ color: '#fff' }}>
              {ft.hold_duration_tenths > 0 ? `${ft.hold_duration_tenths.toFixed(1)}t` : '—'}
            </span>
          </div>
          <div>
            <span style={{ color: '#888' }}>Stability: </span>
            <span style={{ color: '#fff' }}>{ft.hold_stability || '—'}</span>
          </div>
          <div>
            <span style={{ color: '#888' }}>Fingers: </span>
            <span style={{ color: '#fff' }}>
              {ft.finger_direction !== 'not_visible' ? ft.finger_direction?.replace(/_/g, ' ') : '—'}
            </span>
          </div>
          <div>
            <span style={{ color: '#888' }}>Wrist: </span>
            <span style={{ color: '#fff' }}>
              {ft.wrist_angle_at_hold != null ? `${Math.round(ft.wrist_angle_at_hold)}°` : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Knee Detail */}
      <div style={cardStyle}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 700, color: '#888' }}>Knee Detail</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12 }}>
          <div>
            <span style={{ color: '#888' }}>L Knee: </span>
            <span style={{ color: '#fff' }}>
              {knee.left_knee_angle != null ? `${Math.round(knee.left_knee_angle)}°` : '—'}
            </span>
          </div>
          <div>
            <span style={{ color: '#888' }}>R Knee: </span>
            <span style={{ color: '#fff' }}>
              {knee.right_knee_angle != null ? `${Math.round(knee.right_knee_angle)}°` : '—'}
            </span>
          </div>
          <div>
            <span style={{ color: '#888' }}>@ Raise: </span>
            <span style={{ color: '#fff' }}>
              {knee.knee_angle_at_raise_start != null ? `${Math.round(knee.knee_angle_at_raise_start)}°` : '—'}
            </span>
          </div>
          <div>
            <span style={{ color: '#888' }}>@ Set: </span>
            <span style={{ color: '#fff' }}>
              {knee.knee_angle_at_set_point != null ? `${Math.round(knee.knee_angle_at_set_point)}°` : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* Confidence */}
      <div style={{ fontSize: 11, color: '#666', textAlign: 'center' }}>
        Confidence: {((attempt.overall_confidence || 0) * 100).toFixed(0)}%
        {attempt.video_quality_notes && (
          <span> · {attempt.video_quality_notes}</span>
        )}
      </div>
    </div>
  );
}
